import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ChordLearningPanel from "@/components/music/ChordLearningPanel";
import ChordBeatCarousel from "@/components/music/ChordBeatCarousel";
import ChordGridComposer from "@/components/music/ChordGridComposer";
import PianoChordMiniDiagram from "@/components/music/PianoChordMiniDiagram";
import GuitarChordDiagram from "@/components/music/GuitarChordDiagram";
import BassTriadDiagram from "@/components/music/BassTriadDiagram";
import PracticeSongPlayer from "@/components/music/PracticeSongPlayer";
import YouTubePracticePlayer from "@/components/music/YouTubePracticePlayer";
import SongStructurePanel from "@/components/SongStructurePanel";
import LyricsKaraokeView from "@/components/LyricsKaraokeView";
import usePracticeChordSync from "@/hooks/usePracticeChordSync";
import { findActiveSectionIndexAtTime, normalizeSectionTimeline } from "@/music/chords/chordSyncUtils";
import { transposeChordSymbol } from "@/music/chords/chordTheoryEngine";
import {
  readLearningInstrumentPreference,
  writeLearningInstrumentPreference,
} from "@/music/chords/learningInstrumentPreference";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowLeftRight,
  Clock3,
  FileDown,
  Gauge,
  Layers3,
  ListMusic,
  Loader2,
  Music2,
  Repeat,
  Search,
  Share2,
  Star,
  Volume2,
  Youtube,
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY_TIMELINE = [];
const RATES = [0.5, 0.75, 1, 1.25, 1.5];

const formatDuration = (seconds = 0) => {
  const safe = Math.max(0, Number(seconds) || 0);
  return safe > 0 ? `${Math.floor(safe / 60)}:${String(Math.floor(safe % 60)).padStart(2, "0")}` : "--:--";
};

const formatConfidence = (value) => `${Math.round((Number(value) || 0) * 100)}%`;

const ToolButton = ({ icon: Icon, label, value, active = false, disabled = false, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-pressed={active || undefined}
    className={`flex min-h-[4.2rem] min-w-[5.25rem] shrink-0 flex-col items-center justify-center gap-1 border-r border-white/10 px-3 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-35 ${
      active ? "bg-cyan-300/12 text-cyan-100" : "text-slate-400 hover:bg-white/[0.055] hover:text-white"
    }`}
  >
    <Icon className="h-4 w-4" />
    <span className="uppercase text-[10px] font-semibold">{label}</span>
    {value && <span className="max-w-20 truncate text-[11px] text-slate-300">{value}</span>}
  </button>
);

const YouTubePracticeSyncPage = () => {
  const { sourceId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, token, user } = useAuth();
  const isAuthenticated = Boolean(user);
  const getOptionalAuthHeaders = useCallback(() => (token ? getAuthHeaders() : {}), [getAuthHeaders, token]);
  const playerRef = useRef(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [source, setSource] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(Boolean(sourceId));
  const [structure, setStructure] = useState(null);
  const [structureLoading, setStructureLoading] = useState(false);
  const [dataError, setDataError] = useState("");
  const [playerReady, setPlayerReady] = useState(false);
  const [embedFailed, setEmbedFailed] = useState(false);
  const [instrument, setInstrument] = useState(() => readLearningInstrumentPreference());
  const [activeTab, setActiveTab] = useState("diagrams");
  const [visualMode, setVisualMode] = useState("animated");
  const [loopEnabled, setLoopEnabled] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [volume, setVolume] = useState(80);
  const [transpose, setTranspose] = useState(0);
  const [capo, setCapo] = useState(0);
  const [cleanupView, setCleanupView] = useState("original");

  const searchCatalog = useCallback(async (event) => {
    event?.preventDefault();
    setSearching(true);
    setDataError("");
    try {
      const response = await axios.get(`${API_URL}/youtube-practice/search`, {
        headers: getOptionalAuthHeaders(),
        params: { q: query.trim() },
      });
      setResults(response.data || []);
      setHasSearched(true);
    } catch (error) {
      setDataError(error.response?.data?.detail || "No fue posible buscar en el catalogo.");
      setResults([]);
      setHasSearched(true);
    } finally {
      setSearching(false);
    }
  }, [getOptionalAuthHeaders, query]);

  useEffect(() => {
    if (!sourceId) {
      setSource(null);
      setProfile(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setDataError("");
    setCleanupView("original");
    setPlayerReady(false);
    setEmbedFailed(false);
    Promise.all([
      axios.get(`${API_URL}/youtube-practice/sources/${sourceId}/details`, { headers: getOptionalAuthHeaders() }),
      axios.get(`${API_URL}/youtube-practice/sources/${sourceId}/profile`, { headers: getOptionalAuthHeaders() }).catch((error) => {
        if (error.response?.status === 404) return { data: null };
        throw error;
      }),
    ])
      .then(([detailsResponse, profileResponse]) => {
        if (cancelled) return;
        setSource(detailsResponse.data);
        setProfile(profileResponse.data);
      })
      .catch((error) => {
        if (cancelled) return;
        setSource(null);
        setProfile(null);
        setDataError(error.response?.data?.detail || "No se pudo cargar la practica.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [getOptionalAuthHeaders, sourceId]);

  // Load structure when global_song_id is available
  useEffect(() => {
    const songId = source?.global_song_id;
    if (!songId || !token) { setStructure(null); return; }
    let cancelled = false;
    setStructureLoading(true);
    axios
      .get(`${API_URL}/global-songs/${songId}/structure`, { headers: getOptionalAuthHeaders() })
      .then((res) => { if (!cancelled) setStructure(res.data); })
      .catch(() => { if (!cancelled) setStructure(null); })
      .finally(() => { if (!cancelled) setStructureLoading(false); });
    return () => { cancelled = true; };
  }, [source?.global_song_id, getOptionalAuthHeaders, token]);

  const durationHint = Number(source?.duration_seconds || 0);
  const cleanupStatus = profile?.chord_cleanup_status || "original";
  // Individual previews from each cleanup service
  const keyboardPreview     = profile?.chords_keyboard_preview?.length     ? profile.chords_keyboard_preview     : null;
  const segmentationPreview = profile?.chords_segmentation_preview?.length ? profile.chords_segmentation_preview : null;
  const combinedPreview     = profile?.clean_combined_preview?.length       ? profile.clean_combined_preview       : null;
  // Legacy cleanup previews (passing chord + triad guard)
  const legacyCleanupPreview =
    profile?.chords_triad_guard_preview?.length
      ? profile.chords_triad_guard_preview
      : profile?.chords_suppressed_preview?.length
        ? profile.chords_suppressed_preview
        : null;
  // Determine which views to offer
  const hasAnyPreview = cleanupStatus === "preview" && (
    keyboardPreview || segmentationPreview || combinedPreview || legacyCleanupPreview
  );
  const availableViews = hasAnyPreview ? [
    ["original",    "Original"],
    ...(keyboardPreview     ? [["keyboard",    "Keyboard"]]    : []),
    ...(segmentationPreview ? [["segmentation","Segmentation"]] : []),
    ...(combinedPreview     ? [["combined",    "Combined"]]    : []),
    ...(!keyboardPreview && !segmentationPreview && !combinedPreview && legacyCleanupPreview ? [["preview", "Clean Preview"]] : []),
  ] : null;
  const rawTimeline = (() => {
    if (cleanupView === "keyboard"     && keyboardPreview)     return keyboardPreview;
    if (cleanupView === "segmentation" && segmentationPreview) return segmentationPreview;
    if (cleanupView === "combined"     && combinedPreview)     return combinedPreview;
    if (cleanupView === "preview"      && legacyCleanupPreview) return legacyCleanupPreview;
    return profile?.chords || EMPTY_TIMELINE;
  })();
  const sync = usePracticeChordSync(playerRef, rawTimeline, { duration: durationHint, enabled: Boolean(sourceId) });
  const displayedChords = useMemo(
    () => sync.chords.map((chord) => ({ ...chord, chord: transposeChordSymbol(chord.chord, transpose) })),
    [sync.chords, transpose]
  );
  const displayedChord = sync.currentIndex >= 0 ? displayedChords[sync.currentIndex]?.chord : null;
  const displayedNextChord = sync.currentIndex >= 0
    ? displayedChords[sync.currentIndex + 1]?.chord || null
    : displayedChords[0]?.chord || null;
  const sections = useMemo(
    () => normalizeSectionTimeline(profile?.sections || source?.sections || [], durationHint),
    [durationHint, profile?.sections, source?.sections]
  );
  const activeSectionIndex = findActiveSectionIndexAtTime(sections, sync.currentTime);
  const activeSection = activeSectionIndex >= 0 ? sections[activeSectionIndex] : null;
  const uniqueChords = useMemo(() => {
    const seen = new Set();
    return displayedChords.filter((chord) => {
      if (seen.has(chord.chord)) return false;
      seen.add(chord.chord);
      return true;
    });
  }, [displayedChords]);
  const controlsDisabled = !playerReady || embedFailed;
  const publicHeader = !isAuthenticated ? (
    <nav className="flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-white/10 bg-white/[0.045] px-4 py-3">
      <Link to="/" className="flex items-center gap-2 text-white">
        <span className="grid h-10 w-10 place-items-center rounded-2xl bg-cyan-300/14 text-cyan-200">
          <Music2 className="h-5 w-5" />
        </span>
        <span className="font-bold">SteelMora</span>
      </Link>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Link to="/" className="rounded-xl border border-white/10 px-3 py-2 text-slate-300 hover:bg-white/8">
          Inicio
        </Link>
        <Link to="/auth" className="rounded-xl bg-cyan-300 px-3 py-2 font-semibold text-slate-950 hover:bg-cyan-200">
          Iniciar sesiÃ³n
        </Link>
      </div>
    </nav>
  ) : null;

  const selectInstrument = (nextInstrument) => {
    setInstrument(writeLearningInstrumentPreference(nextInstrument));
  };

  const cycleInstrument = () => {
    const instruments = ["piano", "guitar", "bass"];
    const position = instruments.indexOf(instrument);
    selectInstrument(instruments[(position + 1) % instruments.length]);
  };

  const handlePlayerStateChange = (state) => {
    sync.handlePlayerStateChange(state);
    if (state === 0 && loopEnabled) {
      sync.seekTo(0);
      playerRef.current?.play?.();
    }
  };

  const handleVolumeChange = (nextVolume) => {
    setVolume(nextVolume);
    playerRef.current?.setVolume?.(nextVolume);
  };

  const handleRateChange = (rate) => {
    setPlaybackRate(rate);
    playerRef.current?.setPlaybackRate?.(rate);
  };

  const cyclePlaybackRate = () => {
    const index = RATES.indexOf(playbackRate);
    handleRateChange(RATES[(index + 1) % RATES.length]);
  };

  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const handlePdfDownload = async () => {
    if (!source?.global_song_id) return;
    if (!isAuthenticated) {
      toast.info("Inicia sesiÃ³n para guardar esta prÃ¡ctica en tu biblioteca.");
      return;
    }
    setDownloadingPdf(true);
    try {
      const res = await axios.get(
        `${API_URL}/global-songs/${source.global_song_id}/export/pdf`,
        { headers: getOptionalAuthHeaders(), responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${source.title || "partitura"}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo descargar el PDF.");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const sharePractice = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: source?.title || "YouTube Practice", url: window.location.href });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success("Enlace copiado");
      }
    } catch (error) {
      if (error.name !== "AbortError") toast.error("No se pudo compartir el enlace");
    }
  };

  if (!sourceId) {
    return (
      <section className="steel-page space-y-6">
        {publicHeader}
        <header className="rounded-3xl border border-white/10 bg-white/[0.045] p-5 sm:p-7">
          <div className="flex items-center gap-3">
            <span className="grid h-12 w-12 place-items-center rounded-2xl bg-cyan-300/14 text-cyan-200">
              <Youtube className="h-6 w-6" />
            </span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-cyan-200/65">SteelMora Studio</p>
              <h1 className="text-2xl font-bold text-white sm:text-3xl">YouTube Practice</h1>
            </div>
          </div>
          <p className="mt-3 max-w-2xl text-sm text-slate-400">
            Busca canciones del catalogo compartido y practica con el reproductor oficial y acordes publicados.
          </p>
          {!isAuthenticated && (
            <p className="mt-2 text-sm text-cyan-100">
              Inicia sesiÃ³n para guardar o crear tus propias prÃ¡cticas.
            </p>
          )}
          <form onSubmit={searchCatalog} className="mt-6 flex flex-col gap-3 sm:flex-row">
            <label className="flex min-h-12 flex-1 items-center gap-3 rounded-2xl border border-white/10 bg-black/20 px-4 focus-within:border-cyan-300/50">
              <Search className="h-5 w-5 text-slate-400" />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Titulo, artista, canal o video"
                className="h-12 min-w-0 flex-1 bg-transparent text-white outline-none placeholder:text-slate-500"
              />
            </label>
            <button type="submit" disabled={searching} className="min-h-12 rounded-2xl bg-cyan-300 px-6 font-semibold text-slate-950 transition hover:bg-cyan-200 disabled:opacity-60">
              {searching ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : "Buscar"}
            </button>
          </form>
        </header>

        {dataError && (
          <div className="rounded-2xl border border-amber-400/25 bg-amber-400/10 p-4 text-sm text-amber-100">
            <AlertTriangle className="mr-2 inline h-4 w-4" />{dataError}
          </div>
        )}
        {!hasSearched ? (
          <div className="grid min-h-48 place-items-center rounded-3xl border border-dashed border-white/10 bg-white/[0.025] text-center text-slate-400">
            <div>
              <Music2 className="mx-auto mb-3 h-9 w-9 text-cyan-200/45" />
              <p>Busca una cancion para comenzar tu ensayo.</p>
            </div>
          </div>
        ) : results.length === 0 && !searching ? (
          <div className="rounded-3xl border border-white/10 bg-white/[0.045] p-10 text-center">
            <p className="font-semibold text-white">Sin resultados publicados</p>
            <p className="mt-1 text-sm text-slate-400">No encontramos prÃ¡cticas publicadas para esta bÃºsqueda.</p>
          </div>
        ) : (
          <div className="grid gap-3 lg:grid-cols-2" aria-label="Resultados de busqueda">
            {results.map((item) => (
              <article key={item.source_id} className="flex flex-col gap-3 rounded-2xl border border-white/10 bg-white/[0.045] p-3 sm:flex-row sm:items-center">
                <img
                  src={item.thumbnail_url || `https://i.ytimg.com/vi/${item.youtube_video_id}/mqdefault.jpg`}
                  alt=""
                  className="aspect-video w-full rounded-xl object-cover sm:w-36"
                />
                <div className="min-w-0 flex-1">
                  <h2 className="truncate font-semibold text-white">{item.title}</h2>
                  <p className="truncate text-sm text-slate-400">{item.artist || item.channel || "YouTube"}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
                    {item.duration_seconds > 0 && <Badge className="bg-white/8 text-slate-300">{formatDuration(item.duration_seconds)}</Badge>}
                    <Badge className={item.chords_available ? "bg-cyan-300/15 text-cyan-100" : "bg-white/8 text-slate-400"}>
                      {item.chords_available ? "Acordes disponibles" : "Sin acordes publicados"}
                    </Badge>
                    {isAuthenticated && item.catalog_scope === "personal" && <Badge className="bg-violet-400/15 text-violet-100">Tu biblioteca</Badge>}
                  </div>
                </div>
                <button type="button" onClick={() => navigate(`/youtube-practice/${item.source_id}`)} className="min-h-11 rounded-xl bg-cyan-300 px-5 text-sm font-semibold text-slate-950 hover:bg-cyan-200">
                  Practicar
                </button>
              </article>
            ))}
          </div>
        )}
      </section>
    );
  }

  if (loading) {
    return <div className="grid min-h-[55vh] place-items-center text-cyan-200"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  if (!source) {
    return (
      <section className="steel-page space-y-4">
        {publicHeader}
        <div className="mx-auto max-w-lg rounded-3xl border border-white/10 bg-white/[0.045] p-8 text-center">
          <AlertTriangle className="mx-auto mb-3 h-9 w-9 text-amber-300" />
          <h1 className="font-semibold text-white">Practica no disponible</h1>
          <p className="mt-2 text-sm text-slate-400">{dataError || "No encontramos esa fuente de practica."}</p>
          <button type="button" onClick={() => navigate("/youtube-practice")} className="mt-5 rounded-xl bg-cyan-300 px-4 py-2 font-semibold text-slate-950">Volver a buscar</button>
        </div>
      </section>
    );
  }

  return (
    <section className="steel-page space-y-4">
      {publicHeader}
      <div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <main className="min-w-0 space-y-4">
          <header className="flex flex-wrap items-start justify-between gap-3 rounded-3xl border border-white/10 bg-white/[0.045] p-4 sm:p-5">
            <div className="flex min-w-0 items-start gap-3">
              <button type="button" onClick={() => navigate("/youtube-practice")} className="grid min-h-11 min-w-11 place-items-center rounded-xl border border-white/10 text-slate-300 hover:bg-white/8" aria-label="Volver a busqueda">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.2em] text-cyan-200/65">YouTube Practice</p>
                <h1 className="truncate text-xl font-bold text-white sm:text-2xl">{source.title}</h1>
                <p className="truncate text-sm text-slate-400">{source.artist || source.channel || "YouTube"}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {profile?.timing_source && (
                <Badge className={profile.timing_source === "estimated" ? "bg-amber-400/15 text-amber-100" : "bg-cyan-300/15 text-cyan-100"}>
                  {profile.timing_source === "estimated" ? "Tiempos estimados" : "Timeline publicada"}
                </Badge>
              )}
              {cleanupStatus === "clean" && (
                <Badge className="bg-emerald-300/15 text-emerald-100">Clean</Badge>
              )}
              {hasAnyPreview && availableViews && (
                <div className="flex flex-wrap items-center gap-1 rounded-xl border border-white/10 bg-white/[0.045] p-1">
                  <span className="px-2 text-[10px] font-semibold uppercase text-amber-300">
                    Experimental
                  </span>
                  {availableViews.map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setCleanupView(value)}
                      className={`min-h-9 rounded-lg px-3 text-xs font-semibold ${
                        cleanupView === value ? "bg-cyan-300 text-slate-950" : "text-slate-300 hover:text-white"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
              {isAuthenticated && source?.global_song_id && (
                <button
                  type="button"
                  onClick={handlePdfDownload}
                  disabled={downloadingPdf}
                  className="flex min-h-11 items-center gap-2 rounded-xl border border-white/10 px-3 text-sm text-slate-300 hover:bg-white/8 disabled:opacity-50"
                  title="Descargar partitura PDF"
                >
                  {downloadingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
                  PDF
                </button>
              )}
              {!isAuthenticated && (
                <Link to="/auth" className="flex min-h-11 items-center rounded-xl border border-cyan-300/30 px-3 text-sm font-semibold text-cyan-100 hover:bg-cyan-300/10">
                  Inicia sesiÃ³n para guardar
                </Link>
              )}
              <button type="button" onClick={sharePractice} className="flex min-h-11 items-center gap-2 rounded-xl border border-white/10 px-3 text-sm text-slate-300 hover:bg-white/8">
                <Share2 className="h-4 w-4" /> Share
              </button>
            </div>
          </header>

          <PracticeSongPlayer
            currentTime={sync.currentTime}
            duration={sync.duration || durationHint}
            isPlaying={sync.isPlaying}
            progress={sync.progress}
            chords={displayedChords}
            currentIndex={sync.currentIndex}
            loopEnabled={loopEnabled}
            playbackRate={playbackRate}
            volume={volume}
            onTogglePlay={() => (sync.isPlaying ? playerRef.current?.pause?.() : playerRef.current?.play?.())}
            onSeek={sync.seekTo}
            onToggleLoop={() => setLoopEnabled((enabled) => !enabled)}
            onPlaybackRateChange={handleRateChange}
            onVolumeChange={handleVolumeChange}
            disabled={controlsDisabled}
            showTimeline={false}
            showUtilityControls={false}
          />

          <nav className="steel-scroll-soft flex overflow-x-auto rounded-2xl border border-white/10 bg-white/[0.04]" aria-label="Herramientas de practica">
            <ToolButton icon={Clock3} label="Count-in" disabled />
            <ToolButton icon={Volume2} label="Volume" value={`${volume}%`} onClick={() => handleVolumeChange(volume === 0 ? 80 : 0)} />
            <ToolButton icon={Music2} label="Instrument" value={instrument} onClick={cycleInstrument} />
            <ToolButton icon={Repeat} label="Loop" active={loopEnabled} onClick={() => setLoopEnabled((enabled) => !enabled)} />
            <ToolButton icon={Gauge} label="Tempo" value={`${playbackRate}x`} onClick={cyclePlaybackRate} />
            <ToolButton icon={Layers3} label="Capo" value={`${capo}`} onClick={() => setCapo((value) => (value === 6 ? 0 : value + 1))} />
            <ToolButton icon={ArrowLeftRight} label="Transpose" value={transpose > 0 ? `+${transpose}` : `${transpose}`} onClick={() => setTranspose((value) => (value === 6 ? -6 : value + 1))} />
            <ToolButton icon={ListMusic} label="Sections" active={activeTab === "overview"} onClick={() => setActiveTab("overview")} />
          </nav>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <TabsList className="h-11 border border-white/10 bg-white/[0.045] p-1">
                <TabsTrigger value="diagrams">Chord diagrams</TabsTrigger>
                <TabsTrigger value="overview">Chord overview</TabsTrigger>
                <TabsTrigger value="lyrics">Chords &amp; Lyrics</TabsTrigger>
                <TabsTrigger value="karaoke">🎤 Letra</TabsTrigger>
                <TabsTrigger value="structure">Estructura</TabsTrigger>
              </TabsList>
              <div className="flex rounded-xl border border-white/10 bg-white/[0.045] p-1">
                {["animated", "summary"].map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setVisualMode(mode)}
                    className={`min-h-9 rounded-lg px-4 text-sm ${visualMode === mode ? "bg-cyan-300 text-slate-950" : "text-slate-300"}`}
                  >
                    {mode === "animated" ? "Animated" : "Summary"}
                  </button>
                ))}
              </div>
            </div>

            <TabsContent value="diagrams" className="mt-0 space-y-4">
              {!sync.chords.length ? (
                <div className="grid min-h-48 place-items-center rounded-2xl border border-white/10 bg-white/[0.04] p-6 text-center">
                  <div>
                    <Music2 className="mx-auto mb-3 h-8 w-8 text-slate-500" />
                    <p className="font-medium text-white">Esta prÃ¡ctica aÃºn no tiene acordes publicados.</p>
                    <p className="mt-1 text-sm text-slate-400">Puedes reproducir el video mientras se publica una linea musical.</p>
                  </div>
                </div>
              ) : (
                <>
                  <section className="space-y-3 rounded-2xl border border-white/10 bg-white/[0.04] p-3">
                    <div className="flex flex-wrap items-center gap-3 px-1 text-sm">
                      <span className="text-slate-400">Ahora</span>
                      <strong className="text-2xl text-cyan-100">{displayedChord || "--"}</strong>
                      <span className="ml-auto text-slate-400">Siguiente</span>
                      <strong className="text-lg text-violet-200">{displayedNextChord || "--"}</strong>
                    </div>
                    <ChordBeatCarousel
                      mode="practice"
                      events={displayedChords}
                      bpm={profile?.bpm || source.bpm || 120}
                      currentTime={sync.currentTime}
                      duration={sync.duration || durationHint}
                      onSeek={sync.seekTo}
                      autoScroll={sync.isPlaying}
                    />
                  </section>
                  {visualMode === "animated" ? (
                    <ChordLearningPanel
                      currentChord={displayedChord}
                      nextChord={displayedNextChord}
                      timeToNextChord={sync.timeToNextChord}
                      currentTime={sync.currentTime}
                      chords={displayedChords}
                      instrumentMode={instrument}
                      onInstrumentModeChange={selectInstrument}
                      compact
                      capo={capo}
                    />
                  ) : (
                    <div className="steel-scroll-soft flex gap-4 overflow-x-auto rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                      {uniqueChords.map((chord) => (
                        instrument === "piano" ? <PianoChordMiniDiagram key={chord.chord} chordName={chord.chord} />
                          : instrument === "guitar" ? <GuitarChordDiagram key={chord.chord} chordName={chord.chord} capo={capo} compact />
                            : <div key={chord.chord} className="min-w-[20rem]"><BassTriadDiagram chordName={chord.chord} compact /></div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </TabsContent>

            <TabsContent value="overview" className="mt-0 space-y-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              {displayedChords.length ? (
                <ChordGridComposer
                  mode="practice"
                  events={displayedChords}
                  bpm={profile?.bpm || source.bpm || 120}
                  currentTime={sync.currentTime}
                  duration={sync.duration || durationHint}
                  onSeek={sync.seekTo}
                  autoFollow={sync.isPlaying}
                />
              ) : (
                <p className="text-sm text-slate-400">Esta prÃ¡ctica aÃºn no tiene acordes publicados.</p>
              )}
              <div className="flex flex-wrap gap-2">
                {uniqueChords.length ? uniqueChords.map((chord) => (
                  <Badge key={chord.chord} className="border border-cyan-300/15 bg-cyan-300/10 text-cyan-100">{chord.chord}</Badge>
                )) : null}
              </div>
              {sections.length > 0 && (
                <div className="mt-4 border-t border-white/10 pt-4">
                  <p className="mb-2 text-xs uppercase text-slate-500">Secciones</p>
                  <div className="flex gap-2 overflow-x-auto">
                    {sections.map((section, index) => (
                      <button key={`${section.start}-${section.name}`} type="button" onClick={() => sync.seekTo(section.start)} className={`rounded-lg border px-3 py-2 text-sm ${index === activeSectionIndex ? "border-cyan-300/40 bg-cyan-300/12 text-cyan-100" : "border-white/10 text-slate-300"}`}>
                        {section.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="lyrics" className="mt-0 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              {profile?.lyrics_sections?.length ? profile.lyrics_sections.map((section) => (
                <div key={`${section.section}-${section.start_time || 0}`} className="mb-3">
                  <p className="text-xs uppercase text-cyan-200/70">{section.section}</p>
                  <p className="whitespace-pre-wrap text-sm text-slate-200">{section.text}</p>
                </div>
              )) : <p className="text-sm text-slate-400">No hay letra sincronizada publicada para esta cancion.</p>}
            </TabsContent>

            <TabsContent value="karaoke" className="mt-0 rounded-2xl border border-white/10 bg-white/[0.04]">
              <LyricsKaraokeView
                structure={structure}
                currentTime={sync.currentTime}
                onLineClick={sync.seekTo}
              />
            </TabsContent>

            <TabsContent value="structure" className="mt-0">
              <SongStructurePanel
                structure={structure}
                currentTime={sync.currentTime}
                onSectionClick={sync.seekTo}
                loading={structureLoading}
              />
            </TabsContent>
          </Tabs>
        </main>

        <aside className="space-y-4 xl:self-start">
          <YouTubePracticePlayer
            ref={playerRef}
            videoId={source.youtube_video_id}
            onReady={() => {
              setPlayerReady(true);
              playerRef.current?.setVolume?.(volume);
              playerRef.current?.setPlaybackRate?.(playbackRate);
              sync.syncNow(true);
            }}
            onStateChange={handlePlayerStateChange}
            onError={() => {
              setEmbedFailed(true);
              sync.handlePlayerStateChange(2);
            }}
          />
          <section className="rounded-2xl border border-white/10 bg-white/[0.045] p-4">
            <div className="mb-4 flex items-center justify-between gap-2">
              <h2 className="font-semibold text-white">Song Details</h2>
              <Badge className={rawTimeline.length ? "bg-cyan-300/15 text-cyan-100" : "bg-white/8 text-slate-400"}>
                {rawTimeline.length
                ? (cleanupView === "original" ? "Acordes" : `Preview · ${cleanupView}`)
                : "Sin acordes"}
              </Badge>
            </div>
            <dl className="space-y-3 text-sm">
              <div className="flex gap-3 border-b border-white/10 pb-3"><dt className="w-20 text-xs uppercase text-slate-500">Rating</dt><dd className="flex gap-1 text-slate-500">{[1, 2, 3, 4, 5].map((star) => <Star key={star} className="h-4 w-4" />)}</dd></div>
              <div className="flex gap-3"><dt className="w-20 text-xs uppercase text-slate-500">Chords</dt><dd className="flex flex-wrap gap-1">{uniqueChords.length ? uniqueChords.slice(0, 6).map((chord) => <Badge key={chord.chord} className="bg-white/8 text-slate-200">{chord.chord}</Badge>) : "--"}</dd></div>
              <div className="flex gap-3"><dt className="w-20 text-xs uppercase text-slate-500">Key</dt><dd className="text-slate-200">{profile?.key || source.key || "--"}</dd></div>
              <div className="flex gap-3"><dt className="w-20 text-xs uppercase text-slate-500">BPM</dt><dd className="text-slate-200">{profile?.bpm || source.bpm || "--"}</dd></div>
              <div className="flex gap-3"><dt className="w-20 text-xs uppercase text-slate-500">Genre</dt><dd className="text-slate-200">{profile?.genre || source.genre || "--"}</dd></div>
              <div className="flex gap-3"><dt className="w-20 text-xs uppercase text-slate-500">Section</dt><dd className="text-slate-200">{activeSection?.name || "--"}</dd></div>
            </dl>
            {Number.isFinite(Number(profile?.confidence)) && (
              <p className="mt-4 border-t border-white/10 pt-3 text-xs text-slate-400">Confidence {formatConfidence(profile.confidence)}</p>
            )}
            {!isAuthenticated && (
              <p className="mt-4 rounded-xl border border-cyan-300/20 bg-cyan-300/10 p-3 text-xs text-cyan-100">
                Inicia sesiÃ³n para guardar esta prÃ¡ctica en tu biblioteca.
              </p>
            )}
            <p className="mt-4 rounded-xl border border-white/10 bg-black/20 p-3 text-xs text-slate-400">
              Reproduccion mediante el player oficial de YouTube. Los acordes provienen del catalogo SteelMora.
            </p>
          </section>
        </aside>
      </div>
    </section>
  );
};

export default YouTubePracticeSyncPage;
